// Lane Type management service - API integration with fallback to mock
import { getCurrentConfig, getApiScope } from '../config';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';

// Define LaneType interface based on API response
export interface LaneType {
  lane_type_id: number;
  checkpoint_id: number;
  checkpoint_name: string;
  terminal_name: string;
  type_name: string;
  description?: string;
  purpose?: string | null;
  is_active: boolean;
  created_date: string;
}

interface CreateLaneTypeData {
  checkpoint_id: number;
  type_name: string;
  description?: string;
  purpose?: string;
}

interface UpdateLaneTypeData {
  checkpoint_id?: number;
  type_name?: string;
  description?: string;
  purpose?: string;
}

class LaneTypeService {
  private baseURL: string;
  private useMockData: boolean;
  
  constructor() {
    const config = getCurrentConfig();
    this.baseURL = config.apiBaseUrl;
    this.useMockData = config.useMockData;
  }

  // Private method to make API requests with error handling
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const baseUrl = this.baseURL;
      
      // Fix double slash issue - simple URL construction
      let url = baseUrl + endpoint;
      url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
      url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
      
      // Get access token if available (same pattern as api.ts)
      let token: string | null = null;
      try {
        const config = getCurrentConfig();
        
        if (config.auth.enableAuthentication) {
          const { msalInstance } = await import('../contexts/AuthContext');
          const accounts = msalInstance.getAllAccounts();
          
          if (accounts.length > 0) {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: [getApiScope()], // Use scope from .env
              account: accounts[0]
            });
            token = tokenResponse.accessToken;
          }
        }
      } catch (error) {
        // Silent fail for token acquisition
      }
      
      const headers: Record<string, string> = {
        ...DEFAULT_HEADERS,
      };
      
      // Add any additional headers from options
      if (options?.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }
      
      // Add authorization header if token is available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // ✅ ADD TIMEOUT (30 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      try {
        const response = await fetch(url, {
          headers,
          ...options,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return response.json();
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        // ✅ BETTER ERROR MESSAGES
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timeout (30s) - backend may be slow or unresponsive');
        }
        if (fetchError.message === 'Failed to fetch') {
          throw new Error('Network error - check CORS, backend status, or connectivity');
        }
        throw fetchError;
      }
    } catch (error: any) {
      console.error('Lane Type API request failed:', {
        endpoint,
        error: error.message,
        stack: error.stack,
      });
      throw error;
    }
  }

  // Mock data for fallback
  private mockData: LaneType[] = [
    {
      lane_type_id: 1,
      checkpoint_id: 1,
      checkpoint_name: "Checkpoint 1A",
      terminal_name: "Terminal 1",
      type_name: "Standard Lane",
      description: "Regular passenger security lane",
      purpose: "APP",
      is_active: true,
      created_date: "2023-01-15T08:00:00Z"
    },
    {
      lane_type_id: 2,
      checkpoint_id: 1,
      checkpoint_name: "Checkpoint 1A", 
      terminal_name: "Terminal 1",
      type_name: "Priority Lane",
      description: "Priority security lane for VIP passengers",
      purpose: "APP",
      is_active: true,
      created_date: "2023-01-15T08:30:00Z"
    },
    {
      lane_type_id: 3,
      checkpoint_id: 2,
      checkpoint_name: "Checkpoint 1A",
      terminal_name: "Terminal 1", 
      type_name: "Family Lane",
      description: "Security lane optimized for families with children",
      purpose: null,
      is_active: true,
      created_date: "2023-02-01T09:00:00Z"
    }
  ];
  
  private nextId = 4;

  async getAllLaneTypes(): Promise<LaneType[]> {
    // Try API first, fallback to mock data on error
    if (!this.useMockData) {
      try {
        const response = await this.request<{ data: { lane_types: LaneType[] } }>(API_ENDPOINTS.LANE_TYPES);
        // API returns data.lane_types array
        return response.data?.lane_types || [];
      } catch (error) {
        console.warn('API request failed, falling back to mock data:', error);
      }
    }
    
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 500));
    return [...this.mockData.filter(lt => lt.is_active)];
  }

  async getLaneTypesByCheckpoint(checkpointId: number): Promise<LaneType[]> {
    // Try API first, fallback to mock data on error
    if (!this.useMockData) {
      try {
        const response = await this.request<{ data: LaneType[] }>(API_ENDPOINTS.LANE_TYPES_BY_CHECKPOINT(checkpointId));
        return response.data || [];
      } catch (error) {
        console.warn('API request failed, falling back to mock data:', error);
      }
    }
    
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 400));
    return this.mockData.filter(lt => lt.checkpoint_id === checkpointId && lt.is_active);
  }

  async getLaneTypeById(id: number): Promise<LaneType> {
    // Try API first, fallback to mock data on error
    if (!this.useMockData) {
      try {
        return await this.request<LaneType>(API_ENDPOINTS.LANE_TYPE_BY_ID(id));
      } catch (error) {
        console.warn('API request failed, falling back to mock data:', error);
      }
    }
    
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 300));
    const laneType = this.mockData.find(lt => lt.lane_type_id === id && lt.is_active);
    if (!laneType) {
      throw new Error(`Lane type ${id} not found`);
    }
    return { ...laneType };
  }

  async createLaneType(data: CreateLaneTypeData): Promise<LaneType> {
    // Validate input
    if (!data.type_name?.trim()) {
      throw new Error('Lane type name is required');
    }

    if (!data.checkpoint_id) {
      throw new Error('Checkpoint selection is required');
    }

    // Try API first, fallback to mock data on error
    if (!this.useMockData) {
      try {
        return await this.request<LaneType>(API_ENDPOINTS.LANE_TYPES, {
          method: 'POST',
          body: JSON.stringify(data),
        });
      } catch (error) {
        console.warn('API request failed, falling back to mock data:', error);
      }
    }

    // Mock implementation
    // Check for duplicate names within the same checkpoint
    if (this.mockData.some(lt => 
      lt.checkpoint_id === data.checkpoint_id && 
      lt.type_name.toLowerCase() === data.type_name.trim().toLowerCase() &&
      lt.is_active
    )) {
      throw new Error('Lane type name already exists in this checkpoint');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const newLaneType: LaneType = {
      lane_type_id: this.nextId++,
      checkpoint_id: data.checkpoint_id,
      checkpoint_name: "Mock Checkpoint", // Mock value for local testing
      terminal_name: "Mock Terminal", // Mock value for local testing
      type_name: data.type_name.trim(),
      description: data.description?.trim() || undefined,
      purpose: data.purpose?.trim() || undefined,
      is_active: true,
      created_date: new Date().toISOString()
    };
    
    this.mockData.push(newLaneType);
    return { ...newLaneType };
  }

  async updateLaneType(id: number, data: UpdateLaneTypeData): Promise<LaneType> {
    // Validate input
    if (data.type_name !== undefined && !data.type_name?.trim()) {
      throw new Error('Lane type name cannot be empty');
    }

    // Try API first, fallback to mock data on error
    if (!this.useMockData) {
      try {
        return await this.request<LaneType>(API_ENDPOINTS.LANE_TYPE_BY_ID(id), {
          method: 'PUT',
          body: JSON.stringify(data),
        });
      } catch (error) {
        console.warn('API request failed, falling back to mock data:', error);
      }
    }

    // Mock implementation
    const laneTypeIndex = this.mockData.findIndex(lt => lt.lane_type_id === id && lt.is_active);
    if (laneTypeIndex === -1) {
      throw new Error(`Lane type ${id} not found`);
    }

    // Check for duplicate names within the same checkpoint (excluding current lane type)
    if (data.type_name && data.checkpoint_id) {
      if (this.mockData.some(lt => 
        lt.lane_type_id !== id &&
        lt.checkpoint_id === data.checkpoint_id && 
        lt.type_name.toLowerCase() === data.type_name!.trim().toLowerCase() &&
        lt.is_active
      )) {
        throw new Error('Lane type name already exists in this checkpoint');
      }
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 600));

    const updatedLaneType = {
      ...this.mockData[laneTypeIndex],
      ...(data.checkpoint_id !== undefined && { checkpoint_id: data.checkpoint_id }),
      ...(data.type_name !== undefined && { type_name: data.type_name.trim() }),
      ...(data.description !== undefined && { description: data.description?.trim() || undefined }),
      ...(data.purpose !== undefined && { purpose: data.purpose?.trim() || undefined })
    };

    this.mockData[laneTypeIndex] = updatedLaneType;
    return { ...updatedLaneType };
  }

  async deleteLaneType(id: number): Promise<void> {
    // Try API first, fallback to mock data on error
    if (!this.useMockData) {
      try {
        await this.request<{ message: string }>(API_ENDPOINTS.LANE_TYPE_BY_ID(id), {
          method: 'DELETE',
        });
        return;
      } catch (error) {
        console.warn('API request failed, falling back to mock data:', error);
      }
    }

    // Mock implementation
    const laneTypeIndex = this.mockData.findIndex(lt => lt.lane_type_id === id && lt.is_active);
    if (laneTypeIndex === -1) {
      throw new Error(`Lane type ${id} not found`);
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // Soft delete - set is_active to false
    this.mockData[laneTypeIndex].is_active = false;
  }

  // Validation helpers
  validateLaneTypeName(name: string): { isValid: boolean; message?: string } {
    if (!name?.trim()) {
      return { isValid: false, message: 'Lane type name is required' };
    }
    
    if (name.trim().length < 2) {
      return { isValid: false, message: 'Lane type name must be at least 2 characters long' };
    }
    
    if (name.trim().length > 100) {
      return { isValid: false, message: 'Lane type name must be less than 100 characters' };
    }
    
    return { isValid: true };
  }

  validateDescription(description: string): { isValid: boolean; message?: string } {
    if (description && description.trim().length > 500) {
      return { isValid: false, message: 'Description must be less than 500 characters' };
    }
    
    return { isValid: true };
  }
}

export const laneTypeService = new LaneTypeService();
export default laneTypeService;
